import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { ToolbarConfig } from '../../models/toolbarconfig';
import { BeepService } from '../../services/beep.service';
import { ToolbarService } from '../../services/toolbar.service';
import { EventService } from '../../services/event.service';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Router } from '@angular/router';
import { ModalService } from '../../services/modal.service';
import { TourService } from '../../services/tour.service';
import { AppUrls } from '../../app.urls';
import firebase from 'firebase/compat';
import {LocationService} from '../../services/location.service';
import {MatSnackBar} from '@angular/material/snack-bar';
import {TranslateService} from '@ngx-translate/core';
import {GeoLocation} from '../../models/geo-location';

type LocStatus = 'gps_off' | 'gps_not_fixed' | 'gps_fixed';

@Component({
  selector: 'app-toolbar',
  templateUrl: './toolbar.component.html',
  styleUrls: ['./toolbar.component.scss']
})
export class ToolbarComponent implements OnInit, OnDestroy {

  toolbarConfig: ToolbarConfig;
  soundsEnabled: boolean;
  private toolbarConfigSubscription?: Subscription;
  private soundsEnabledSubscription?: Subscription;
  private userSubscription?: Subscription;
  private locationStateSubscription?: Subscription;
  private user: firebase.User;

  locationIconName: LocStatus = 'gps_not_fixed';
  locationBusy = false;

  constructor(private translate: TranslateService,
              private modal: ModalService,
              private auth: AngularFireAuth,
              private snackBar: MatSnackBar,
              private router: Router,
              private tour: TourService,
              private location: LocationService,
              private beepService: BeepService,
              private events: EventService,
              public toolbar: ToolbarService) {
  }

  ngOnInit(): void {
    this.toolbarConfigSubscription = this.toolbar.toolbarConfig$.subscribe(v => this.toolbarConfig = v);
    this.soundsEnabledSubscription = this.beepService.soundsEnabled.subscribe(v => this.soundsEnabled = v);
    this.userSubscription = this.auth.user.subscribe(u => this.user = u);
    this.locationStateSubscription = this.location.state.subscribe((state) => {
      if (state === 'not-permitted') {
        this.locationIconName = 'gps_off';
      } else if (state === 'pending') {
        this.locationIconName = 'gps_not_fixed';
      } else {
        this.locationIconName = 'gps_fixed';
      }
    });
  }

  ngOnDestroy(): void {
    this.toolbarConfigSubscription.unsubscribe();
    this.soundsEnabledSubscription.unsubscribe();
    this.userSubscription.unsubscribe();
    this.locationStateSubscription?.unsubscribe();
  }

  onBack(): void {
    if (this.toolbarConfig.backAction) {
      this.toolbarConfig.backAction();
    }
  }

  toggleSounds(): void {
    this.beepService.enableSounds(!this.soundsEnabled);
  }

  locationButtonClicked(): void {

    const locationLabel = this.translate.instant('toolbar.location');
    const locationState = this.location.state.value;
    if (locationState === 'not-permitted' || locationState === 'pending') {

      // try to get a live location
      this.location.updateCurrentLocation()
        .then(pos => {
          const coords = pos.coords;
          this.snackBar.open(`${locationLabel}: ${coords.latitude.toFixed(4)}° N, ${coords.longitude.toFixed(4)}° E`);
        })
        .catch(err => {
          this.snackBar.open(this.location.getLocalizedErrorMessage(err));
        });
    } else {

      // show the current location
      const coords: GeoLocation = locationState;
      this.snackBar.open(`${locationLabel}: ${coords.latitude.toFixed(4)}° N, ${coords.longitude.toFixed(4)}° E`);
    }
  }

  settingsButtonClicked(): void {
    this.events.settingsButtonClicked.next();
  }

  callButtonClicked(): void {
    this.events.callButtonClicked.next();
  }

  refreshButtonClicked(): void {
    this.events.refreshButtonClicked.next();
  }

  get shouldShowLogout(): boolean {
    return this.toolbarConfig && this.toolbarConfig.showLogout && !!this.user;
  }

  logoutButtonClicked(): void {
    this.modal.showLogoutModal().then(result => {
      if (result) {
        this.auth.signOut().then(() => {
          this.tour.clearTour().then(() => {
            this.router.navigate([AppUrls.ROOT, AppUrls.Login], { replaceUrl: true });
          });
        });
      }
    });
  }
}
